import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, useTheme } from "@mui/material";
import { useCallback, useMemo, type MouseEvent } from "react";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import type {
	CellStyle,
	CellStyleFunc,
	ColDef,
	ColGroupDef,
	ICellRendererParams,
} from "ag-grid-community";

import type {
	InspectionWithValues,
	InspectionValue,
	ProfileInspectionsResult,
} from "../../models/Inspection";
import { formatAngle, formatCheckedReading } from "../utils/specUnits";
import { formatMeasurement } from "@/shared/utils/measurementPrecision";
import { getSummaryMeasurementStyle } from "../../utils/summaryCellStyle";
import { YES_NO_CODES } from "../constants/inspectionChecks";
import type { ProfileItem } from "@/pages/configuration/models/ProfileItem";
import { MeasurementValue } from "../../shared/components/MeasurementValue";
import {
	getSummaryItems,
	hasWeightColumn,
	weightApplies,
} from "../../utils/summaryItems";
import { isAverageRow, type SummaryRow } from "../models/SummaryRow";
import { isInspectionEvaluated } from "../../utils/hasInspectionTolerances";

// Shared values stay keyed by numeric ID; codes match readings across revisions.
const readingAt = (row: SummaryRow | undefined, code: string) => {
	if (!row) return undefined;
	if (isAverageRow(row))
		return {
			NumericValue:
				row.rolling.dimensions.find((item) => item.Code === code)?.AvgValue ??
				null,
		};
	return Object.values(row.values).find((item) => item.Code === code);
};
const weightAt = (row: SummaryRow | undefined) =>
	row
		? isAverageRow(row)
			? row.rolling.summary?.AvgLbsPerFoot
			: row.MeasuredLbsPerFoot
		: null;

const formatReading = (
	reading?: Pick<InspectionValue, "NumericValue"> & {
		Checked?: boolean | null;
		Code?: string;
	},
	angle = false,
): string => {
	if (reading?.Checked != null)
		return formatCheckedReading(reading.Code ?? "", reading.Checked);
	if (reading?.NumericValue == null) return "-";
	return angle
		? formatAngle(reading.NumericValue)
		: formatMeasurement(reading.NumericValue);
};

const MeasurementCell = ({
	value,
}: ICellRendererParams<SummaryRow, string>) => (
	<MeasurementValue>{value}</MeasurementValue>
);

interface SummaryActions {
	inspections?: InspectionWithValues[];
	profileItems?: ProfileItem[];
	onEdit?: (inspection: InspectionWithValues) => void;
	onDelete?: (inspection: InspectionWithValues) => void;
	disabled?: boolean;
	applicableItemsBySpec?: ProfileInspectionsResult["applicableItemsBySpec"];
}

export const useM1RollingTableData = ({
	onEdit,
	onDelete,
	disabled = false,
	applicableItemsBySpec,
	profileItems,
	inspections,
}: SummaryActions = {}) => {
	const theme = useTheme();
	const summaryItems = useMemo(
		() => getSummaryItems({ inspections, applicableItemsBySpec }),
		[inspections, applicableItemsBySpec],
	);
	const itemCodes = useMemo(
		() => new Set(summaryItems.map((item) => item.Code)),
		[summaryItems],
	);
	const showWeight = hasWeightColumn(summaryItems, inspections);
	const itemsByCode = useMemo(
		() => new Map(profileItems?.map((item) => [item.Code, item])),
		[profileItems],
	);
	const itemHeader = useCallback(
		(code: string) => ({
			headerName: itemsByCode.get(code)?.DisplayName ?? code,
			headerTooltip: itemsByCode.get(code)?.Name,
		}),
		[itemsByCode],
	);
	const appliesTo = useCallback(
		(data: SummaryRow | undefined, code: string) => {
			if (!data || isAverageRow(data)) return undefined;
			const specApplies = applicableItemsBySpec?.[data.ProfileSpecId]?.some(
				(item) => item.Code === code,
			);
			return code === "WEIGHT"
				? weightApplies(specApplies, data.MeasuredLbsPerFoot)
				: specApplies;
		},
		[applicableItemsBySpec],
	);
	const formatMeasurement = useCallback(
		(data: SummaryRow | undefined, code: string, angle = false) => {
			if (appliesTo(data, code) === false) return "";
			return formatReading(
				code === "WEIGHT"
					? { NumericValue: weightAt(data) ?? null }
					: readingAt(data, code),
				angle,
			);
		},
		[appliesTo],
	);
	const measurementCellStyle = useCallback<CellStyleFunc<SummaryRow>>(
		({ data, colDef }) => {
			if (data && isAverageRow(data))
				return {
					textAlign: "center",
					fontWeight: 600,
					color: "inherit",
					backgroundColor: "transparent",
				};
			const code = colDef.colId ?? "";
			const reading =
				data && !isAverageRow(data)
					? code === "WEIGHT"
						? {
								NumericValue: data.MeasuredLbsPerFoot,
								Ltl: data.MinLbsPerFoot,
								Utl: data.MaxLbsPerFoot,
								Checked: null,
							}
						: Object.values(data.values).find((item) => item.Code === code)
					: undefined;
			// Without a specification nothing can be judged, so dashes stay black.
			const evaluated =
				!data ||
				isInspectionEvaluated(data, applicableItemsBySpec, {
					withLegDelta: false,
				});
			return getSummaryMeasurementStyle({
				reading,
				applies: appliesTo(data, code),
				evaluated,
				errorColor: theme.palette.error.main,
				missingValueColor: YES_NO_CODES.has(code)
					? theme.palette.text.disabled
					: evaluated
						? theme.palette.error.main
						: theme.palette.text.primary,
				notApplicableBackground: theme.palette.action.disabledBackground,
			});
		},
		[
			appliesTo,
			applicableItemsBySpec,
			theme.palette.error.main,
			theme.palette.text.disabled,
			theme.palette.text.primary,
			theme.palette.action.disabledBackground,
		],
	);
	const measurementCell = useMemo(
		() => ({
			cellStyle: measurementCellStyle,
			cellRenderer: MeasurementCell,
		}),
		[measurementCellStyle],
	);
	// Keep M1's grouping and formatting, then select the largest displayed revision.
	const allColumnDefs = useMemo<
		(ColDef<SummaryRow> | ColGroupDef<SummaryRow>)[]
	>(
		() => [
			{
				colId: "InspectedAt",
				headerName: "Sample At",
				width: 164,
				minWidth: 164,
				cellStyle: ({ data }): CellStyle =>
					data && isAverageRow(data)
						? {
								textAlign: "center",
								fontWeight: 600,
								textTransform: "uppercase",
								fontSize: "11px",
							}
						: { textAlign: "center" },
				cellRenderer: ({ data }: { data?: SummaryRow }) => {
					if (!data) return "-";
					if (isAverageRow(data)) return "Current Rolling Avg";
					return (
						dxtToLocalServerTime(data.InspectedAt, "MM/dd/yyyy HH:mm") || "-"
					);
				},
			},
			{
				colId: "SectionName",
				headerName: "Section",
				minWidth: 80,
				flex: 2,
				valueGetter: ({ data }) => data?.SectionName ?? "-",
			},
			{
				colId: "HeatName",
				headerName: "Heat",
				minWidth: 80,
				flex: 2,
				valueGetter: ({ data }) => data?.HeatName ?? "-",
			},
			{
				colId: "WEIGHT",
				headerName: "Weight",
				minWidth: 56,
				flex: 1,
				valueGetter: ({ data }) => formatMeasurement(data, "WEIGHT"),
				...measurementCell,
			},
			{
				groupId: "m1-summary-rads",
				headerName: "Rad's",
				marryChildren: true,
				children: [
					{
						colId: "RA",
						...itemHeader("RA"),
						minWidth: 44,
						flex: 1,
						valueGetter: ({ data }) => formatMeasurement(data, "RA"),
						...measurementCell,
					},
					{
						colId: "RB",
						...itemHeader("RB"),
						minWidth: 44,
						flex: 1,
						valueGetter: ({ data }) => formatMeasurement(data, "RB"),
						...measurementCell,
					},
					{
						colId: "RC",
						...itemHeader("RC"),
						minWidth: 44,
						flex: 1,
						valueGetter: ({ data }) => formatMeasurement(data, "RC"),
						...measurementCell,
					},
					{
						colId: "RD",
						...itemHeader("RD"),
						minWidth: 44,
						flex: 1,
						valueGetter: ({ data }) => formatMeasurement(data, "RD"),
						...measurementCell,
					},
					{
						colId: "RALL",
						...itemHeader("RALL"),
						minWidth: 100,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "RALL"),
						...measurementCell,
					},
				],
			},
			{
				groupId: "m1-summary-leg-angles",
				headerName: "Leg Angles",
				marryChildren: true,
				children: [
					{
						colId: "LA1",
						...itemHeader("LA1"),
						minWidth: 72,
						flex: 1.5,
						valueGetter: ({ data }) => formatMeasurement(data, "LA1", true),
						...measurementCell,
					},
					{
						colId: "LA2",
						...itemHeader("LA2"),
						minWidth: 72,
						flex: 1.5,
						valueGetter: ({ data }) => formatMeasurement(data, "LA2", true),
						...measurementCell,
					},
				],
			},
			{
				groupId: "m1-summary-linear-dims",
				headerName: "Linear Dim's",
				marryChildren: true,
				children: [
					{
						colId: "1",
						...itemHeader("1"),
						minWidth: 44,
						flex: 1,
						valueGetter: ({ data }) => formatMeasurement(data, "1"),
						...measurementCell,
					},
					{
						colId: "2",
						...itemHeader("2"),
						minWidth: 44,
						flex: 1,
						valueGetter: ({ data }) => formatMeasurement(data, "2"),
						...measurementCell,
					},
					{
						colId: "3",
						...itemHeader("3"),
						minWidth: 44,
						flex: 1,
						valueGetter: ({ data }) => formatMeasurement(data, "3"),
						...measurementCell,
					},
					{
						colId: "4",
						...itemHeader("4"),
						minWidth: 44,
						flex: 1,
						valueGetter: ({ data }) => formatMeasurement(data, "4"),
						...measurementCell,
					},
				],
			},
			{
				groupId: "m1-summary-gauges",
				headerName: "Gauges",
				marryChildren: true,
				children: [
					{
						colId: "5",
						...itemHeader("5"),
						minWidth: 32,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "5"),
						...measurementCell,
					},
					{
						colId: "6",
						...itemHeader("6"),
						minWidth: 32,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "6"),
						...measurementCell,
					},
					{
						colId: "7",
						...itemHeader("7"),
						minWidth: 32,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "7"),
						...measurementCell,
					},
					{
						colId: "8",
						...itemHeader("8"),
						minWidth: 32,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "8"),
						...measurementCell,
					},
					{
						colId: "9",
						...itemHeader("9"),
						minWidth: 32,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "9"),
						...measurementCell,
					},
					{
						colId: "10",
						...itemHeader("10"),
						minWidth: 32,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "10"),
						...measurementCell,
					},
					{
						colId: "11",
						...itemHeader("11"),
						minWidth: 32,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "11"),
						...measurementCell,
					},
					{
						colId: "12",
						...itemHeader("12"),
						minWidth: 32,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "12"),
						...measurementCell,
					},
					{
						colId: "13",
						...itemHeader("13"),
						minWidth: 32,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "13"),
						...measurementCell,
					},
					{
						colId: "14",
						...itemHeader("14"),
						minWidth: 32,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "14"),
						...measurementCell,
					},
				],
			},
			{
				groupId: "m1-summary-stencil",
				headerName: "Stencil",
				marryChildren: true,
				children: [
					{
						colId: "STLEG",
						...itemHeader("STLEG"),
						minWidth: 60,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "STLEG"),
						...measurementCell,
					},
					{
						colId: "STHGT",
						...itemHeader("STHGT"),
						minWidth: 60,
						flex: 1,
						valueGetter: ({ data }) => formatMeasurement(data, "STHGT"),
						...measurementCell,
					},
				],
			},
			{
				groupId: "m1-summary-samples",
				headerName: "Samples",
				marryChildren: true,
				children: [
					{
						colId: "BEND",
						...itemHeader("BEND"),
						minWidth: 76,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "BEND"),
						...measurementCell,
					},
					{
						colId: "SHOT",
						...itemHeader("SHOT"),
						minWidth: 80,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "SHOT"),
						...measurementCell,
					},
					{
						colId: "MAGNA",
						...itemHeader("MAGNA"),
						minWidth: 80,
						flex: 0.5,
						valueGetter: ({ data }) => formatMeasurement(data, "MAGNA"),
						...measurementCell,
					},
				],
			},
			{
				colId: "actions",
				headerName: "",
				width: 90,
				minWidth: 90,
				sortable: false,
				resizable: false,
				cellRenderer: ({ data }: { data?: SummaryRow }) => {
					if (!data || isAverageRow(data)) return null;
					const handleClick = (
						event: MouseEvent<HTMLButtonElement>,
						callback?: SummaryActions["onEdit"],
					) => {
						event.stopPropagation();
						if (!disabled) callback?.(data);
					};
					return (
						<Box
							className="inspection-actions"
							onDoubleClick={(event) => event.stopPropagation()}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
								gap: 0.5,
							}}
						>
							<IconButton
								aria-label="Edit inspection"
								size="small"
								disabled={disabled || !onEdit}
								onClick={(event) => handleClick(event, onEdit)}
							>
								<EditIcon fontSize="small" color="primary" />
							</IconButton>
							<IconButton
								aria-label="Delete inspection"
								size="small"
								disabled={disabled || !onDelete}
								onClick={(event) => handleClick(event, onDelete)}
							>
								<DeleteIcon fontSize="small" color="error" />
							</IconButton>
						</Box>
					);
				},
			},
		],
		[
			onEdit,
			onDelete,
			disabled,
			measurementCell,
			formatMeasurement,
			itemHeader,
		],
	);

	const columnDefs = useMemo(
		() =>
			allColumnDefs.flatMap<ColDef<SummaryRow> | ColGroupDef<SummaryRow>>(
				(column) => {
					if ("children" in column) {
						const children = column.children.filter(
							(child) => "colId" in child && itemCodes.has(child.colId ?? ""),
						);
						return children.length ? [{ ...column, children }] : [];
					}
					if (column.colId === "WEIGHT" && !showWeight) return [];
					return [column];
				},
			),
		[allColumnDefs, itemCodes, showWeight],
	);

	return { columnDefs };
};
